<template>
  <div
    class="brand-rich-text-editor"
    :class="[
      variantClass,
      {
        'brand-rich-text-editor--focused': isFocused,
        'brand-rich-text-editor--disabled': disabled,
        'brand-rich-text-editor--error': hasError,
      }
    ]"
  >
    <!-- Frame bounds the sticky toolbar to the text box itself, so it never
         stays pinned past the box (over the counter / error messages). -->
    <div class="brand-rich-text-editor__frame">
      <!-- Toolbar -->
      <div
        v-if="!disabled"
        ref="toolbarRef"
        class="brand-rich-text-editor__toolbar"
        :class="{ 'brand-rich-text-editor__toolbar--mobile': smAndDown }"
      >
        <v-btn
          v-if="hasFeature('bold')"
          icon
          variant="text"
          :size="smAndDown ? 'x-small' : 'small'"
          :class="{ 'is-active': editor?.isActive('bold') }"
          :disabled="disabled"
          @click="editor?.chain().focus().toggleBold().run()"
        >
          <v-icon :size="smAndDown ? 18 : 20">mdi-format-bold</v-icon>
        </v-btn>

        <v-btn
          v-if="hasFeature('italic')"
          icon
          variant="text"
          :size="smAndDown ? 'x-small' : 'small'"
          :class="{ 'is-active': editor?.isActive('italic') }"
          :disabled="disabled"
          @click="editor?.chain().focus().toggleItalic().run()"
        >
          <v-icon :size="smAndDown ? 18 : 20">mdi-format-italic</v-icon>
        </v-btn>

        <v-btn
          v-if="hasFeature('underline')"
          icon
          variant="text"
          :size="smAndDown ? 'x-small' : 'small'"
          :class="{ 'is-active': editor?.isActive('underline') }"
          :disabled="disabled"
          @click="editor?.chain().focus().toggleUnderline().run()"
        >
          <v-icon :size="smAndDown ? 18 : 20">mdi-format-underline</v-icon>
        </v-btn>

        <v-divider v-if="hasTextFormattingFeatures && hasStructureFeatures" vertical class="mx-1" />

        <v-btn
          v-if="hasFeature('link')"
          icon
          variant="text"
          :size="smAndDown ? 'x-small' : 'small'"
          :class="{ 'is-active': editor?.isActive('link') }"
          :disabled="disabled"
          @click="toggleLink"
        >
          <v-icon :size="smAndDown ? 18 : 20">mdi-link-variant</v-icon>
        </v-btn>

        <v-divider v-if="hasFeature('link') && hasListFeatures" vertical class="mx-1" />

        <v-btn
          v-if="hasFeature('bulletList')"
          icon
          variant="text"
          :size="smAndDown ? 'x-small' : 'small'"
          :class="{ 'is-active': editor?.isActive('bulletList') }"
          :disabled="disabled"
          @click="editor?.chain().focus().toggleBulletList().run()"
        >
          <v-icon :size="smAndDown ? 18 : 20">mdi-format-list-bulleted</v-icon>
        </v-btn>

        <v-btn
          v-if="hasFeature('orderedList')"
          icon
          variant="text"
          :size="smAndDown ? 'x-small' : 'small'"
          :class="{ 'is-active': editor?.isActive('orderedList') }"
          :disabled="disabled"
          @click="editor?.chain().focus().toggleOrderedList().run()"
        >
          <v-icon :size="smAndDown ? 18 : 20">mdi-format-list-numbered</v-icon>
        </v-btn>

        <v-divider v-if="hasListFeatures && hasBlockFeatures" vertical class="mx-1" />

        <v-btn
          v-if="hasFeature('blockquote')"
          icon
          variant="text"
          :size="smAndDown ? 'x-small' : 'small'"
          :class="{ 'is-active': editor?.isActive('blockquote') }"
          :disabled="disabled"
          @click="editor?.chain().focus().toggleBlockquote().run()"
        >
          <v-icon :size="smAndDown ? 18 : 20">mdi-format-quote-close</v-icon>
        </v-btn>

        <v-btn
          v-if="hasFeature('code')"
          icon
          variant="text"
          :size="smAndDown ? 'x-small' : 'small'"
          :class="{ 'is-active': editor?.isActive('codeBlock') }"
          :disabled="disabled"
          @click="editor?.chain().focus().toggleCodeBlock().run()"
        >
          <v-icon :size="smAndDown ? 18 : 20">mdi-code-tags</v-icon>
        </v-btn>

        <!-- Table dropdown -->
        <DropdownHybrid
          v-if="hasFeature('table')"
          ref="tableDropdown"
          v-model="tableMenuOpen"
          :menu-props="tableMenuProps"
          :sheet-title="$t('editor.table.title') || 'Table'"
        >
          <template #activator="{ props: activatorProps }">
            <v-btn
              icon
              variant="text"
              :size="smAndDown ? 'x-small' : 'small'"
              :class="{ 'is-active': editor?.isActive('table') }"
              :disabled="disabled"
              v-bind="activatorProps"
            >
              <v-icon :size="smAndDown ? 18 : 20">mdi-table</v-icon>
            </v-btn>
          </template>

          <v-list bg-color="neutral25" class="py-3 table-dropdown-list">
            <!-- Insert new table -->
            <v-list-item v-if="!isInTable" @click="insertTable">
              <template #prepend>
                <v-icon class="text-h5 mr-3">mdi-table-plus</v-icon>
              </template>
              <v-list-item-title class="text-neutral900 font-weight-medium">{{ $t('editor.table.insert') || 'Insert Table' }}</v-list-item-title>
            </v-list-item>

            <!-- Table manipulation (only when inside a table) -->
            <template v-if="isInTable">
              <v-list-item @click="addRowBefore">
                <template #prepend>
                  <v-icon class="text-h5 mr-3">mdi-table-row-plus-before</v-icon>
                </template>
                <v-list-item-title class="text-neutral900 font-weight-medium">{{ $t('editor.table.add_row_before') || 'Add Row Before' }}</v-list-item-title>
              </v-list-item>

              <v-list-item @click="addRowAfter">
                <template #prepend>
                  <v-icon class="text-h5 mr-3">mdi-table-row-plus-after</v-icon>
                </template>
                <v-list-item-title class="text-neutral900 font-weight-medium">{{ $t('editor.table.add_row_after') || 'Add Row After' }}</v-list-item-title>
              </v-list-item>

              <v-list-item @click="addColumnBefore">
                <template #prepend>
                  <v-icon class="text-h5 mr-3">mdi-table-column-plus-before</v-icon>
                </template>
                <v-list-item-title class="text-neutral900 font-weight-medium">{{ $t('editor.table.add_column_before') || 'Add Column Before' }}</v-list-item-title>
              </v-list-item>

              <v-list-item @click="addColumnAfter">
                <template #prepend>
                  <v-icon class="text-h5 mr-3">mdi-table-column-plus-after</v-icon>
                </template>
                <v-list-item-title class="text-neutral900 font-weight-medium">{{ $t('editor.table.add_column_after') || 'Add Column After' }}</v-list-item-title>
              </v-list-item>

              <v-divider class="my-1" color="neutral50" />

              <v-list-item @click="toggleHeaderRow">
                <template #prepend>
                  <v-icon class="text-h5 mr-3">mdi-table-headers-eye</v-icon>
                </template>
                <v-list-item-title class="text-neutral900 font-weight-medium">{{ $t('editor.table.toggle_header') || 'Toggle Header Row' }}</v-list-item-title>
              </v-list-item>

              <v-divider class="my-1" color="neutral50" />

              <v-list-item class="text-red400" @click="deleteRow">
                <template #prepend>
                  <v-icon class="text-h5 mr-3">mdi-table-row-remove</v-icon>
                </template>
                <v-list-item-title class="font-weight-medium">{{ $t('editor.table.delete_row') || 'Delete Row' }}</v-list-item-title>
              </v-list-item>

              <v-list-item class="text-red400" @click="deleteColumn">
                <template #prepend>
                  <v-icon class="text-h5 mr-3">mdi-table-column-remove</v-icon>
                </template>
                <v-list-item-title class="font-weight-medium">{{ $t('editor.table.delete_column') || 'Delete Column' }}</v-list-item-title>
              </v-list-item>

              <v-list-item class="text-red400" @click="deleteTable">
                <template #prepend>
                  <v-icon class="text-h5 mr-3">mdi-table-remove</v-icon>
                </template>
                <v-list-item-title class="font-weight-medium">{{ $t('editor.table.delete_table') || 'Delete Table' }}</v-list-item-title>
              </v-list-item>
            </template>
          </v-list>
        </DropdownHybrid>
      </div>

      <!-- Editor Content -->
      <div class="brand-rich-text-editor__content">
        <EditorContent
          :editor="editor"
          class="brand-rich-text-editor__editor"
        />
        <div
          v-if="!editor?.getText() && placeholder"
          class="brand-rich-text-editor__placeholder"
        >
          {{ placeholder }}
        </div>
      </div>
    </div>

    <!-- Character counter -->
    <div v-if="maxlength != null" class="brand-rich-text-editor__counter">
      <span class="text-caption" :class="counterColorClass">{{ counterText }}</span>
    </div>

    <!-- Error messages -->
    <div v-if="hasError && errorMessages.length" class="brand-rich-text-editor__errors">
      <span v-for="(error, index) in errorMessages" :key="index" class="text-error text-caption">
        {{ error }}
      </span>
    </div>

    <!-- Link Dialog -->
    <DialogHybrid
      ref="linkDialogRef"
      v-model="linkDialog"
      :dialog-props="{ maxWidth: 400 }"
    >
      <template #sheet-prepend>
        <div class="px-4 py-3">
          <h4 class="text-h5 font-weight-semibold mb-0">
            {{ $t('form.field.link.label') || 'Add Link' }}
          </h4>
        </div>
      </template>

      <v-container class="pa-4 pa-md-6">
        <h4 class="text-h5 font-weight-semibold mb-4 d-none d-md-block">
          {{ $t('form.field.link.label') || 'Add Link' }}
        </h4>

        <BrandTextField
          v-model="linkUrl"
          variant="basic"
          :placeholder="$t('form.field.url.label') || 'URL'"
          class="mb-4"
          @keydown.enter="applyLink"
        />

        <div class="d-flex flex-column">
          <BrandButton
            variant="primary"
            size="large"
            block
            class="mb-3"
            @click="applyLink"
          >
            {{ $t('cta.apply') || 'Apply' }}
          </BrandButton>
          <BrandButton
            v-if="editor?.isActive('link')"
            variant="secondary"
            size="large"
            block
            color="red25"
            class="mb-3 text-red600"
            @click="removeLink"
          >
            {{ $t('cta.remove') || 'Remove' }}
          </BrandButton>
          <BrandButton
            variant="secondary"
            size="large"
            block
            @click="linkDialog = false"
          >
            {{ $t('cta.cancel') || 'Cancel' }}
          </BrandButton>
        </div>
      </v-container>
    </DialogHybrid>
  </div>
</template>

<script>
// All available features - declared in regular script block for use in defineProps
const ALL_FEATURES = ['bold', 'italic', 'underline', 'link', 'bulletList', 'orderedList', 'blockquote', 'code', 'table']

// No toolbar on screen (disabled editor): prosemirror keeps its own scroll defaults.
const NO_CLEARANCE = 0

export default {
  inheritAttrs: false,
  ALL_FEATURES,
}
</script>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useDisplay } from 'vuetify'
import { Editor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import { Table } from '@tiptap/extension-table'
import { TableRow } from '@tiptap/extension-table-row'
import { TableCell } from '@tiptap/extension-table-cell'
import { TableHeader } from '@tiptap/extension-table-header'
import DropdownHybrid from '@/components/global/DropdownHybrid.vue'
import DialogHybrid from '@/components/global/DialogHybrid.vue'
import BrandTextField from '@/components/global/brand/BrandTextField.vue'
import BrandButton from '@/components/global/brand/BrandButton.vue'
import { useCharacterCounter } from '@/composables/useCharacterCounter'

// Use Vuetify display composable for responsive design
const { smAndDown } = useDisplay()

// Props
const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  variant: {
    type: String,
    default: 'basic',
  },
  // Array of enabled features. If not provided, all features are enabled.
  // Available: 'bold', 'italic', 'underline', 'link', 'bulletList', 'orderedList', 'blockquote', 'code'
  enabledFeatures: {
    type: Array,
    default: () => ALL_FEATURES,
    validator: value => value.every(f => ALL_FEATURES.includes(f)),
  },
  // Validation rules (same format as Vuetify)
  rules: {
    type: Array,
    default: () => [],
  },
  // Min height for editor content area
  minHeight: {
    type: [String, Number],
    default: 200,
  },
  // Max character length (counts plain text, not HTML)
  maxlength: {
    type: Number,
    default: null,
  },
})

// Emits
const emit = defineEmits(['update:modelValue', 'focus', 'blur'])

// State
const editor = ref(null)
const isFocused = ref(false)
const linkDialog = ref(false)
const linkUrl = ref('')
const linkSelection = ref(null) // Store selection when opening link dialog
const validationErrors = ref([])
const tableMenuOpen = ref(false)
const tableDropdown = ref(null)
const linkDialogRef = ref(null)
const plainTextLength = ref(0)
const toolbarRef = ref(null)
// Space the pinned toolbar covers: its own height plus the inset it parks at.
// Measured rather than hardcoded: the toolbar is `flex-wrap: wrap` and grows to
// a second row on narrow screens, and the inset differs per surface.
const toolbarClearance = ref(NO_CLEARANCE)
let toolbarResizeObserver = null

// Character counter (always called unconditionally, safe when maxlength is null)
const { counterText, counterColorClass } = useCharacterCounter(plainTextLength, computed(() => props.maxlength))

// Computed
const variantClass = computed(() => {
  if (props.variant === 'basic') return 'brand-rich-text-editor--basic'
  return ''
})

const hasError = computed(() => validationErrors.value.length > 0)

const errorMessages = computed(() => validationErrors.value)

const hasFeature = feature => props.enabledFeatures.includes(feature)

const hasTextFormattingFeatures = computed(() =>
  ['bold', 'italic', 'underline'].some(f => hasFeature(f))
)

const hasStructureFeatures = computed(() =>
  ['link', 'bulletList', 'orderedList', 'blockquote', 'code'].some(f => hasFeature(f))
)

const hasListFeatures = computed(() =>
  ['bulletList', 'orderedList'].some(f => hasFeature(f))
)

const hasBlockFeatures = computed(() =>
  ['blockquote', 'code', 'table'].some(f => hasFeature(f))
)

const minHeightStyle = computed(() => {
  const height = typeof props.minHeight === 'number' ? `${props.minHeight}px` : props.minHeight
  return height
})

// Clearance kept between the caret and the pinned toolbar when ProseMirror scrolls
// the selection into view. prosemirror-view defaults scrollMargin to 5px, which would
// park the caret behind the sticky toolbar while typing near the top of the viewport.
// Falls back to those defaults while unmeasured or when the toolbar is hidden
// (disabled), since prosemirror only keeps its default for an absent prop.
const editorProps = computed(() => {
  if (!toolbarClearance.value) return {}

  const offsets = { top: toolbarClearance.value, right: 0, bottom: 0, left: 0 }
  return { scrollMargin: offsets, scrollThreshold: offsets }
})

// Menu props for table dropdown
const tableMenuProps = computed(() => ({
  location: 'bottom',
  origin: 'top',
  offset: [0, 4],
  minWidth: 220,
}))

// Methods
const validate = () => {
  const content = editor.value?.getHTML() || ''
  const errors = []

  for (const rule of props.rules) {
    const result = rule(content)
    if (result !== true && typeof result === 'string') {
      errors.push(result)
    }
  }

  validationErrors.value = errors
  return errors.length === 0
}

const toggleLink = () => {
  // Store the current selection before opening the dialog
  if (editor.value) {
    const { from, to } = editor.value.state.selection
    linkSelection.value = { from, to }
  }

  if (editor.value?.isActive('link')) {
    // Get current link URL
    linkUrl.value = editor.value.getAttributes('link').href || ''
  } else {
    linkUrl.value = ''
  }
  linkDialog.value = true
}

const closeLinkDialog = () => {
  linkDialogRef.value?.close()
}

const applyLink = () => {
  if (linkUrl.value && editor.value) {
    // Ensure URL has protocol
    let url = linkUrl.value.trim()
    if (!/^https?:\/\//i.test(url) && !/^mailto:/i.test(url) && !/^tel:/i.test(url)) {
      url = `https://${url}`
    }

    // Restore the selection and apply the link
    if (linkSelection.value) {
      editor.value
        .chain()
        .focus()
        .setTextSelection(linkSelection.value)
        .setLink({ href: url })
        .run()
    }
  }
  closeLinkDialog()
  linkUrl.value = ''
  linkSelection.value = null
}

const removeLink = () => {
  if (editor.value && linkSelection.value) {
    editor.value
      .chain()
      .focus()
      .setTextSelection(linkSelection.value)
      .unsetLink()
      .run()
  }
  closeLinkDialog()
  linkUrl.value = ''
  linkSelection.value = null
}

// Table methods
const closeTableDropdown = () => {
  tableDropdown.value?.close()
}

const insertTable = () => {
  editor.value?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
  closeTableDropdown()
}

const addRowBefore = () => {
  editor.value?.chain().focus().addRowBefore().run()
  closeTableDropdown()
}

const addRowAfter = () => {
  editor.value?.chain().focus().addRowAfter().run()
  closeTableDropdown()
}

const addColumnBefore = () => {
  editor.value?.chain().focus().addColumnBefore().run()
  closeTableDropdown()
}

const addColumnAfter = () => {
  editor.value?.chain().focus().addColumnAfter().run()
  closeTableDropdown()
}

const deleteRow = () => {
  editor.value?.chain().focus().deleteRow().run()
  closeTableDropdown()
}

const deleteColumn = () => {
  editor.value?.chain().focus().deleteColumn().run()
  closeTableDropdown()
}

const deleteTable = () => {
  editor.value?.chain().focus().deleteTable().run()
  closeTableDropdown()
}

const toggleHeaderRow = () => {
  editor.value?.chain().focus().toggleHeaderRow().run()
  closeTableDropdown()
}

const isInTable = computed(() => editor.value?.isActive('table') || false)

const focus = () => {
  editor.value?.chain().focus().run()
}

const blur = () => {
  editor.value?.commands.blur()
}

const getHTML = () => {
  return editor.value?.getHTML() || ''
}

const getText = () => {
  return editor.value?.getText() || ''
}

const isEmpty = () => {
  return editor.value?.isEmpty || true
}

// Initialize editor
const initEditor = () => {
  // Destroy existing editor if any (prevents duplicate extension warnings on hot reload)
  if (editor.value) {
    editor.value.destroy()
    editor.value = null
  }

  editor.value = new Editor({
    content: props.modelValue || '',
    editable: !props.disabled,
    editorProps: editorProps.value,
    extensions: [
      StarterKit.configure({
        heading: false, // Disable for notes (keep it simple)
        horizontalRule: false,
        strike: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }),
      Table.configure({
        resizable: false, // Keep simple for mobile
        HTMLAttributes: {
          class: 'rich-text-table',
        },
      }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML()
      // Emit empty string if editor only has empty paragraph
      const isEmpty = ed.isEmpty || html === '<p></p>'
      emit('update:modelValue', isEmpty ? '' : html)
      // Update plain text length for character counter
      plainTextLength.value = ed.getText()?.length ?? 0
      // Validate on update
      validate()
    },
    onFocus: () => {
      isFocused.value = true
      emit('focus')
    },
    onBlur: () => {
      isFocused.value = false
      emit('blur')
      // Validate on blur
      validate()
    },
  })
}

// Watch for external modelValue changes
watch(() => props.modelValue, newValue => {
  const currentContent = editor.value?.getHTML()
  // Only update if content is different to avoid cursor jumping
  if (newValue !== currentContent && !(newValue === '' && currentContent === '<p></p>')) {
    editor.value?.commands.setContent(newValue || '')
  }
})

// Watch for disabled changes
watch(() => props.disabled, disabled => {
  editor.value?.setEditable(!disabled)
})

// editorProps is read at construction time, so push later measurements into the live view
watch(editorProps, value => {
  editor.value?.setOptions({ editorProps: value })
})

// Re-observes when the toolbar element itself comes and goes (it is v-if="!disabled")
watch(toolbarRef, element => {
  toolbarResizeObserver?.disconnect()

  if (!element) {
    toolbarClearance.value = NO_CLEARANCE
    return
  }

  // offsetHeight, not contentRect: the toolbar's border is part of what covers
  // the caret. Plus its resolved sticky inset -- where the page is the scrollport
  // the bar parks that far down and so covers that much more. Re-read here
  // because the only thing that changes the inset (rotation, notch) also
  // resizes the bar.
  toolbarResizeObserver = new ResizeObserver(([entry]) => {
    toolbarClearance.value = entry.target.offsetHeight
      + (parseFloat(getComputedStyle(entry.target).top) || NO_CLEARANCE)
  })
  toolbarResizeObserver.observe(element)
}, { immediate: true })

// Lifecycle
onMounted(() => {
  initEditor()
  // Set initial plain text length for character counter
  plainTextLength.value = editor.value?.getText()?.length ?? 0
})

onBeforeUnmount(() => {
  toolbarResizeObserver?.disconnect()
  toolbarResizeObserver = null
  editor.value?.destroy()
})

// Expose methods
defineExpose({
  focus,
  blur,
  validate,
  getHTML,
  getText,
  isEmpty,
  plainTextLength,
})
</script>

<style lang="scss" scoped>
// Keeps the pinned toolbar above the editor content, which is `position: relative`
// and later in DOM order. Stays below StickyActionBar (3) so page-level action bars win.
$toolbar-sticky-z-index: 2;

.brand-rich-text-editor {
  position: relative;

  &--basic {
    .brand-rich-text-editor__toolbar {
      background-color: rgb(var(--v-theme-neutral30));
      border: 1px solid rgba(0, 0, 0, 0.15);
      border-bottom: none;
      border-radius: 12px 12px 0 0;
      padding: 4px 8px;
      display: flex;
      align-items: center;
      gap: 2px;
      flex-wrap: wrap;
      // Follows the user down long notes so formatting stays reachable. Bounded by
      // __frame, so it detaches exactly at the text box's bottom edge.
      //
      // Where it pins depends on what scrolls it, and only the scroll container
      // knows that: a scrolling element declares `--sticky-top: 0` because its own
      // top edge already sits below every fixed page chrome, and any content above
      // the bar is clipped by that box. Everything else falls back to the page's
      // layout inset. `--v-layout-top` is exact on every surface that hosts this
      // editor -- the focus / focus-detail / fullscreen layouts render no
      // `v-app-bar`, so App.vue's safe-area `v-system-bar` is the only fixed
      // layout item. Put this editor on a route whose layout has an app bar with
      // `scroll-behavior="hide"` and that variable goes stale (see useStickyTop);
      // that surface then has to set `--sticky-top` itself.
      position: sticky;
      top: var(--sticky-top, var(--v-layout-top, 0px));
      z-index: $toolbar-sticky-z-index;
      // iOS Safari repaints sticky elements late during momentum scrolling, so
      // note text draws over the bar for a frame or two. Its own compositor layer
      // keeps it painted above the content that scrolls under it.
      transform: translateZ(0);

      .v-btn {
        color: rgb(var(--v-theme-neutral600));
        border-radius: 6px;
        min-width: 32px;

        &.is-active {
          // Almost transparent blue background with darker blue icon
          background-color: rgba(var(--v-theme-blue400), 0.12);
          color: rgb(var(--v-theme-blue600));
        }

        &:hover:not(.is-active) {
          background-color: rgba(var(--v-theme-neutral600), 0.1);
        }

        &:hover.is-active {
          background-color: rgba(var(--v-theme-blue400), 0.18);
        }
      }

      // Mobile-specific styles
      &--mobile {
        padding: 2px 4px;
        gap: 1px;

        .v-btn {
          min-width: 28px;
        }

        .v-divider {
          margin-left: 2px;
          margin-right: 2px;
        }
      }
    }

    .brand-rich-text-editor__content {
      position: relative;
      display: flex;
      flex-direction: column;
      background-color: rgb(var(--v-theme-neutral0));
      border: 1px solid rgba(0, 0, 0, 0.15);
      border-radius: 0 0 12px 12px;
      min-height: v-bind(minHeightStyle);
      padding: 14px 16px;
    }

    &.brand-rich-text-editor--focused {
      .brand-rich-text-editor__toolbar {
        border-color: rgb(var(--v-theme-blue400));
      }

      .brand-rich-text-editor__content {
        border-color: rgb(var(--v-theme-blue400));
      }
    }

    &.brand-rich-text-editor--error {
      .brand-rich-text-editor__toolbar {
        border-color: rgb(var(--v-theme-error));
      }

      .brand-rich-text-editor__content {
        border-color: rgb(var(--v-theme-error));
      }
    }

    &.brand-rich-text-editor--disabled {
      opacity: 0.6;
      pointer-events: none;

      .brand-rich-text-editor__content {
        background-color: rgb(var(--v-theme-neutral30));
      }
    }
  }

  &__editor {
    flex: 1;
    display: flex;
    flex-direction: column;
    outline: none;

    :deep(.ProseMirror) {
      flex: 1;
      outline: none;

      // Not `> * + *`: a universal subject after `+` makes the browser restyle the
      // whole subtree of any element that gets a new previous sibling, page-wide
      // (a big tier list board on every touch, as the pull-to-refresh inserts its
      // box before it).
      > :not(:first-child) {
        margin-top: 0.75em;
      }

      // Text formatting
      strong {
        font-weight: 600;
      }

      em {
        font-style: italic;
      }

      u {
        text-decoration: underline;
      }

      // Links
      a {
        color: rgb(var(--v-theme-blue400));
        text-decoration: underline;
        cursor: pointer;

        &:hover {
          color: rgb(var(--v-theme-blue600));
        }
      }

      // Lists
      ul,
      ol {
        padding-left: 1.5em;
        margin: 0.5em 0;
      }

      ul {
        list-style-type: disc;
      }

      ol {
        list-style-type: decimal;
      }

      li {
        margin: 0.25em 0;

        p {
          margin: 0;
        }
      }

      // Blockquote
      blockquote {
        border-left: 3px solid rgb(var(--v-theme-neutral200));
        padding-left: 1em;
        margin: 0.5em 0;
        color: rgb(var(--v-theme-neutral600));
        font-style: italic;
      }

      // Code block
      pre {
        background-color: rgb(var(--v-theme-neutral100));
        border-radius: 8px;
        padding: 12px 16px;
        margin: 0.5em 0;
        overflow-x: auto;

        code {
          background: none;
          padding: 0;
          font-size: 0.875em;
          font-family: 'Fira Code', 'Monaco', 'Consolas', monospace;
          color: rgb(var(--v-theme-neutral900));
        }
      }

      // Inline code
      code {
        background-color: rgb(var(--v-theme-neutral100));
        border-radius: 4px;
        padding: 2px 6px;
        font-size: 0.875em;
        font-family: 'Fira Code', 'Monaco', 'Consolas', monospace;
      }

      // Paragraphs
      p {
        margin: 0;
        line-height: 1.6;
      }

      // Empty paragraph placeholder styling
      p.is-editor-empty:first-child::before {
        color: rgb(var(--v-theme-neutral400));
        content: attr(data-placeholder);
        float: left;
        height: 0;
        pointer-events: none;
      }

      // Tables
      table.rich-text-table {
        border-collapse: collapse;
        margin: 0.5em 0;
        overflow: hidden;
        table-layout: fixed;
        width: 100%;
        border-radius: 8px;
        border: 1px solid rgb(var(--v-theme-neutral200));

        th,
        td {
          border: 1px solid rgb(var(--v-theme-neutral200));
          padding: 8px 12px;
          vertical-align: top;
          box-sizing: border-box;
          min-width: 60px;

          > * {
            margin: 0;
          }

          p {
            margin: 0;
          }
        }

        th {
          background-color: rgb(var(--v-theme-neutral50));
          font-weight: 600;
          text-align: left;
        }

        td {
          background-color: transparent;
        }

        // Selected cells
        .selectedCell::after {
          background: rgba(var(--v-theme-blue400), 0.2);
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
          z-index: 1;
        }
      }
    }
  }

  &__placeholder {
    position: absolute;
    top: 14px;
    left: 16px;
    color: rgb(var(--v-theme-neutral400));
    pointer-events: none;
    line-height: 1.6;
  }

  &__counter {
    padding: 4px 12px 0;
    text-align: right;
  }

  &__errors {
    padding: 4px 12px;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
}
</style>

<style lang="scss">
// Table dropdown list styles - unscoped to work with Vuetify portal
.table-dropdown-list {
  .v-list-item__prepend {
    max-width: 36px;
  }
}

// Dark mode specific styles - unscoped to ensure proper cascade
.v-theme--dark {
  .brand-rich-text-editor--basic {
    .brand-rich-text-editor__toolbar {
      // No background override: the scoped `neutral30` already resolves per theme.
      // It must stay opaque -- the toolbar is sticky and note text scrolls under it.
      border-color: rgb(var(--v-theme-neutral50));

      .v-btn {
        &.is-active {
          // Slightly more visible on dark backgrounds
          background-color: rgba(var(--v-theme-blue400), 0.2);
          color: rgb(var(--v-theme-blue300));
        }

        &:hover.is-active {
          background-color: rgba(var(--v-theme-blue400), 0.28);
        }
      }
    }

    .brand-rich-text-editor__content {
      background-color: rgb(var(--v-theme-neutral0));
      border-color: rgb(var(--v-theme-neutral50));
    }

    // Disabled content background needs no override either -- same reason as above.
  }

  .brand-rich-text-editor__editor {
    .ProseMirror {
      blockquote {
        border-left-color: rgb(var(--v-theme-neutral400));
      }

      pre {
        background-color: rgb(var(--v-theme-neutral200));
      }

      code {
        background-color: rgb(var(--v-theme-neutral200));
      }

      // Dark mode table styles
      table.rich-text-table {
        border-color: rgb(var(--v-theme-neutral400));

        th,
        td {
          border-color: rgb(var(--v-theme-neutral400));
        }

        th {
          background-color: rgb(var(--v-theme-neutral200));
        }
      }
    }
  }
}
</style>
